import type {KeyTextField} from '@prismicio/client'
import {createContext, useEffect, useState} from 'react'
import {useEffectOnce, useLocalStorage} from 'react-use'
import {matchesAnyPattern, parsePatterns} from 'src/common/utils/patternMatcher'

type NotificationContextType = {
  disableNotification: () => void
  isActive?: boolean
}

type NotificationContextProviderProps = {
  children: React.ReactNode
  showOnAllPages?: boolean
  includedUids?: KeyTextField
  includedPatterns?: KeyTextField
  notificationId?: KeyTextField
  excludedUids?: KeyTextField
  excludedPatterns?: KeyTextField
  pageUid?: string
}

type NotificationData = {
  isActive: boolean
  isDismissed: boolean
  activeNotificationId: string
}

const initialNotificationData: NotificationData = {
  isActive: false,
  isDismissed: false,
  activeNotificationId: '',
}

export const NotificationContext = createContext<NotificationContextType | null>(null)

export const NotificationContextProvider = ({
  children,
  showOnAllPages = true,
  includedUids,
  includedPatterns,
  notificationId: currentNotificationId,
  excludedUids,
  excludedPatterns,
  pageUid,
}: NotificationContextProviderProps) => {
  const currentUid = pageUid || ''
  const parsedExcludedUids = parsePatterns(excludedUids)
  const parsedIncludedUids = parsePatterns(includedUids)
  const hasExcludedUids = parsedExcludedUids.length > 0
  const hasExcludedPatterns = !!excludedPatterns?.length

  const parsedIncludePatterns = parsePatterns(includedPatterns)
  const parsedExcludePatterns = parsePatterns(excludedPatterns)

  const isIncluded =
    showOnAllPages ||
    (!!currentUid && parsedIncludedUids.length > 0 && parsedIncludedUids.includes(currentUid)) ||
    (!!currentUid && !!includedPatterns?.length && matchesAnyPattern(currentUid, parsedIncludePatterns))

  const isPathExcluded =
    !!currentUid &&
    ((hasExcludedUids && parsedExcludedUids.includes(currentUid)) ||
      (hasExcludedPatterns && matchesAnyPattern(currentUid, parsedExcludePatterns)))

  const activePath = isIncluded && !isPathExcluded

  const [notificationData, setNotificationData] = useLocalStorage<NotificationData>('notification-data')
  const {isActive, isDismissed, activeNotificationId} = notificationData || initialNotificationData
  const [isMounted, setIsMounted] = useState(false)

  const disableNotification = () => {
    setNotificationData({
      isActive: false,
      isDismissed: true,
      activeNotificationId,
    })
  }

  useEffectOnce(() => {
    setIsMounted(true)

    // Activate notification if it's not the latest id
    if (currentNotificationId !== activeNotificationId) {
      setNotificationData({
        isActive: activePath,
        isDismissed: false,
        activeNotificationId: currentNotificationId!,
      })
    }
  })

  // Update isNotificationEnabled when path changes
  useEffect(() => {
    if (!isMounted) {
      return
    }

    if ((isActive === undefined && !isDismissed) || !isDismissed) {
      setNotificationData({
        isActive: activePath,
        isDismissed: false,
        activeNotificationId: currentNotificationId!,
      })
    }
  }, [activePath, currentNotificationId, isActive, isDismissed, isMounted, setNotificationData, pageUid])

  return <NotificationContext.Provider value={{isActive, disableNotification}}>{children}</NotificationContext.Provider>
}
